/**
 * Team administration requests — the existing /admin surface, consumed as
 * it is: accounts, invitations, admissions, role changes, suspension,
 * deauthorization, restoration and the access history.
 */

import { request } from './request.js';

export interface AdminAccount {
  readonly id: string;
  readonly displayName: string;
  readonly role: string;
  readonly status: string;
  readonly createdAt: string;
  readonly disabledAt: string | null;
}

export interface AdminInvitation {
  readonly id: string;
  readonly intendedEmail: string;
  readonly role: string;
  /** Derived server-side: open, accepted, withdrawn, or expired. */
  readonly status: string;
  readonly createdAt: string;
  readonly expiresAt: string;
}

export interface AdminAdmission {
  readonly id: string;
  readonly provider: string;
  readonly providerEmail: string | null;
  readonly providerEmailVerified: boolean;
  readonly providerDisplayName: string | null;
  readonly invitedEmail: string | null;
  readonly invitedRole: string | null;
  readonly status: string;
  readonly createdAt: string;
  readonly expiresAt: string;
}

export function fetchAccounts(): Promise<{ accounts: readonly AdminAccount[] }> {
  return request('GET', '/admin/accounts');
}

export function fetchInvitations(): Promise<{
  invitations: readonly AdminInvitation[];
}> {
  return request('GET', '/admin/invitations');
}

export function fetchAdmissions(): Promise<{ admissions: readonly AdminAdmission[] }> {
  return request('GET', '/admin/admissions');
}

export function createInvitation(input: {
  email: string;
  role: string;
}): Promise<{ invitationUrl: string }> {
  return request('POST', '/admin/invitations', { body: input });
}

export function withdrawInvitation(id: string): Promise<unknown> {
  return request('POST', `/admin/invitations/${encodeURIComponent(id)}/withdraw`);
}

export function decideAdmission(
  id: string,
  decision: 'approve' | 'reject',
): Promise<unknown> {
  return request('POST', `/admin/admissions/${encodeURIComponent(id)}/${decision}`);
}

export function changeAccountRole(id: string, role: string): Promise<unknown> {
  return request('PATCH', `/admin/accounts/${encodeURIComponent(id)}/role`, {
    body: { role },
  });
}

export function suspendAccount(id: string): Promise<unknown> {
  return request('POST', `/admin/accounts/${encodeURIComponent(id)}/suspend`);
}

export function deauthorizeAccount(id: string): Promise<unknown> {
  return request('POST', `/admin/accounts/${encodeURIComponent(id)}/deauthorize`);
}

export interface AccessHistoryEntry {
  readonly id: string;
  readonly occurredAt: string;
  readonly action: string;
  readonly actor: { readonly id: string; readonly displayName: string } | null;
  readonly subject: { readonly id: string; readonly displayName: string } | null;
  readonly detail: Record<string, unknown>;
}

export function fetchAccessHistory(): Promise<{
  entries: readonly AccessHistoryEntry[];
}> {
  return request('GET', '/admin/access-history');
}

export function restoreAccount(id: string): Promise<unknown> {
  return request('POST', `/admin/accounts/${encodeURIComponent(id)}/restore`);
}
